import { api } from "#/lib/api-client";
import type { NotificationItem, NotificationItemRaw, NotificationPageResponse } from "./types";

/**
 * Map backend status to UI status.
 * Backend uses "SENT" to mean "delivered but not read" — there is no "UNREAD" value in the DB.
 */
function normaliseStatus(raw?: string | null): "UNREAD" | "READ" {
  if (!raw) return "UNREAD";
  if (raw === "READ") return "READ";
  return "UNREAD"; // SENT, IN_APP_UNREAD, or anything else → treat as unread
}

/** Strip HTML tags from backend body so raw markup is never shown as text */
function stripHtml(html?: string | null): string | null {
  if (!html) return null;
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&amp;/g,  "&")
    .replace(/&lt;/g,   "<")
    .replace(/&gt;/g,   ">")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g,    " ")
    .trim() || null;
}

/**
 * Map raw backend notification fields to the UI convention:
 *   subjectText → title
 *   bodyText    → message  (HTML stripped)
 *   sentAt      → createdAt
 *   status SENT → UNREAD
 */
function normaliseItem(raw: NotificationItemRaw): NotificationItem {
  return {
    id:            raw.id,
    title:         stripHtml(raw.subjectText) ?? raw.subjectText,
    message:       stripHtml(raw.bodyText),
    status:        normaliseStatus(raw.status),
    createdAt:     raw.sentAt     ?? null,
    readAt:        raw.readAt     ?? null,
    referenceType: raw.referenceType ?? null,
    referenceId:   raw.referenceId   ?? null,
  };
}

/**
 * Unwrap Spring Boot Page responses in any envelope shape:
 *   { content, totalElements, totalPages, number, size, first, last }   (Spring flat)
 *   { content, page: { totalElements, ... } }                            (Spring 3.3+)
 *   { data: { content, ... } }                                           (custom envelope)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function unwrapPage(raw: any): NotificationPageResponse {
  console.debug("[notifications:list] raw →", JSON.parse(JSON.stringify(raw ?? null)));

  const body = raw?.content !== undefined ? raw : (raw?.data ?? raw);
  const meta = body?.page ?? body;

  const result: NotificationPageResponse = {
    content:       ((body?.content ?? []) as NotificationItemRaw[]).map(normaliseItem),
    totalElements: meta?.totalElements ?? meta?.total_elements ?? meta?.total ?? 0,
    totalPages:    meta?.totalPages    ?? meta?.total_pages    ?? 0,
    number:        meta?.number        ?? 0,
    size:          meta?.size          ?? 0,
    first:         meta?.first         ?? (meta?.number === 0),
    last:          meta?.last          ?? false,
  };

  console.debug("[notifications:list] parsed →", result);
  return result;
}

export const notificationApi = {
  async list(page = 0, size = 10): Promise<NotificationPageResponse> {
    const { data } = await api.get(
      "/api/hr/notifications/inbox",
      { params: { page, size } },
    );
    return unwrapPage(data);
  },

  async unreadCount(): Promise<{ unreadCount: number }> {
    const { data } = await api.get("/api/hr/notifications/inbox/unread-count");
    console.debug("[notifications:unreadCount] raw →", JSON.parse(JSON.stringify(data ?? null)));

    if (typeof data === "number") return { unreadCount: data };
    const body = data?.data !== undefined ? data.data : data;
    if (typeof body === "number") return { unreadCount: body };
    const count =
      body?.unreadCount ??
      body?.count       ??
      body?.unread      ??
      body?.total       ??
      0;
    return { unreadCount: count };
  },

  async markRead(messageId: number): Promise<void> {
    // Returns 204 No Content — do not read response body
    await api.put(`/api/hr/notifications/inbox/${messageId}/read`);
  },

  async markAllRead(): Promise<{ marked: number }> {
    const { data } = await api.put<{ marked: number }>(
      "/api/hr/notifications/inbox/mark-all-read",
    );
    return data ?? { marked: 0 };
  },
};
